import { ISession } from '@workspace/apiservice/src/interface/session';
import { session } from '@workspace/apiservice/src';
import { injectServiceWorker, IServiceWorker, IServiceWorkerStatus } from '../../interface/serviceworker';
import { injectUserSession, IStateUserSessionManager } from '../../interface/state';

export function activeSession(state: IStateUserSessionManager): Promise<ISession|null> {
    return new Promise(async function(resolve, reject) {
        try {
            const userState = await state.current();
            let sessionUser = null;
            if ( userState && userState.sessionName && userState.sessionName.length > 0 ) {
                sessionUser = await session(userState.sessionName);
            }
            resolve(sessionUser);
        } catch (error) {
            reject(error);
        }        
    });
}

export function hasSelectedLanguage(state: IStateUserSessionManager): Promise<boolean> {
    return new Promise(async function(resolve, reject) {
        try {
            const userState = await state.current();
            const languageSelected = (userState?.languageId || -1) > 0;
            resolve(languageSelected);
        } catch (error) {
            reject(error);
        }        
    });
}

export function isServiceWorkerRunning(worker: IServiceWorker): Promise<boolean> {
    return new Promise(async function(resolve, reject) {
        try {
            const isRunning = worker.status === IServiceWorkerStatus.Running;
            const workerIsInterceptingFetch = worker.isInterceptingNetworkRequests();

            resolve(isRunning && workerIsInterceptingFetch);
        } catch (error) {
            reject(error);
        }        
    });
}

export function needsMediaDownload(session: ISession): Promise<boolean> {
    return new Promise(async function(resolve, reject) {
        try {
            const allMediaIds = (await session.db.medium().all()).map(medium => medium.id);
            
            const isAllCached = await session.media.isAllMediaCached(allMediaIds);

            resolve(!isAllCached);
        } catch (error) {
            reject(error);
        }        
    });
}

function canDownloadTour(): Promise<boolean> {
    return new Promise(async function(resolve, reject) {
        try {
            const state = await injectUserSession();
            const worker = await injectServiceWorker();

            const isRunning = await isServiceWorkerRunning(worker);
            if ( !isRunning ) { resolve(false); return; }

            /* make sure the user is not on (or before) language-selection, otherwise we'll have to download all-media/all-languages */
            const hasSelectedLanguageCode = await hasSelectedLanguage(state);
            if ( !hasSelectedLanguageCode ) { resolve(false); return; }

            const session = await activeSession(state);
            if ( !session ) { resolve(false); return; }

            const hasMissingMedia = await needsMediaDownload(session);
            if ( !hasMissingMedia ) { resolve(false); return; }

            resolve(true);
        } catch (error) {
            reject(error);
        }
    })
}

function byteSizeToDescription(byteSize: number): string {
    const sizeDivisor = Math.floor( Math.log(byteSize) / Math.log(1024) );
    const decimalPlaces = sizeDivisor > 2 ? 1 : 0;
    const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB'];
    const sizeValue = ( byteSize / Math.pow(1024, sizeDivisor) ).toFixed(decimalPlaces);
    const text = `${sizeValue} ${units[sizeDivisor]}`;
    return text
}

const exports = {
    activeSession,
    isServiceWorkerRunning,
    needsMediaDownload,
    canDownloadTour,
    byteSizeToDescription,
};

export default exports
